import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// MUI
import {
    Assignment,
    ChatBubbleOutline,
    Search as SearchIcon,
    ThumbUpOutlined
} from "@mui/icons-material";
import {
    Box,
    Card,
    CardContent,
    Chip,
    Container,
    FormControl,
    Grid,
    InputAdornment,
    InputLabel,
    MenuItem,
    Pagination,
    Select,
    Stack,
    TextField,
    Typography,
    useTheme
} from "@mui/material";
import axios from "axios";

export default function Homework() {
    const apiURL = process.env.REACT_APP_API;
    const navigate = useNavigate();
    const theme = useTheme();

    const [submissions, setSubmissions] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [searchInput, setSearchInput] = useState("");
    const [search, setSearch] = useState("");
    const [subjectFilter, setSubjectFilter] = useState("all");
    const [subjectOptions, setSubjectOptions] = useState([]);
    const [loading, setLoading] = useState(true);

    const colors = [
        theme.palette.primary.main,
        theme.palette.secondary.main,
        theme.palette.info.main,
        theme.palette.success.main,
        theme.palette.warning.main,
        theme.palette.error.main
    ];

    // Seed the subject filter dropdown from a broad, unfiltered fetch — there
    // is no dedicated public subject-list endpoint for this feature.
    useEffect(() => {
        axios
            .get(`${apiURL}/public/homework/getall?page=1&limit=100`)
            .then((res) => {
                if (res.data.status) {
                    const seen = new Map();
                    (res.data.data || []).forEach((row) => {
                        if (row.subjectid && !seen.has(row.subjectid)) {
                            seen.set(row.subjectid, row.subject_name);
                        }
                    });
                    setSubjectOptions(Array.from(seen, ([subjectid, subject_name]) => ({ subjectid, subject_name })));
                }
            })
            .catch(() => setSubjectOptions([]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        fetchSubmissions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, search, subjectFilter]);

    const fetchSubmissions = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (search) params.append("search", search);
            if (subjectFilter !== "all") params.append("subjectid", subjectFilter);
            params.append("page", currentPage);
            params.append("limit", 12);

            const response = await axios.get(`${apiURL}/public/homework/getall?${params.toString()}`);
            if (response.data.status) {
                const withColors = (response.data.data || []).map((row, index) => ({
                    ...row,
                    color: colors[index % colors.length]
                }));
                setSubmissions(withColors);
                setTotalRecords(response.data.pagination?.total_records || 0);
                setTotalPages(response.data.pagination?.total_pages || 1);
            }
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (event, value) => {
        setCurrentPage(value);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const handleSearchSubmit = (e) => {
        if (e.key === "Enter") {
            setSearch(searchInput);
            setCurrentPage(1);
        }
    };

    return (
        <>
            {/* Hero Section */}
            <Box
                sx={{
                    position: "relative",
                    minHeight: "50vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    overflow: "hidden",
                    pt: { xs: 4, md: 0 }
                }}
            >
                <Box
                    sx={{
                        position: "absolute",
                        top: "-20%",
                        right: "-10%",
                        width: "500px",
                        height: "500px",
                        borderRadius: "50%",
                        background: `${theme.palette.secondary.main}20`,
                        filter: "blur(80px)"
                    }}
                />
                <Box
                    sx={{
                        position: "absolute",
                        bottom: "-20%",
                        left: "-10%",
                        width: "400px",
                        height: "400px",
                        borderRadius: "50%",
                        background: `${theme.palette.info.main}20`,
                        filter: "blur(80px)"
                    }}
                />

                <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
                    <Stack p={2} spacing={3} alignItems="center" textAlign="center">
                        <Box
                            sx={{
                                width: 80,
                                height: 80,
                                borderRadius: "50%",
                                background: "rgba(255, 255, 255, 0.15)",
                                backdropFilter: "blur(10px)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center"
                            }}
                        >
                            <Assignment sx={{ fontSize: "2.5rem", color: "#fff" }} />
                        </Box>

                        <Typography
                            sx={{
                                fontSize: { xs: "2rem", md: "3rem" },
                                fontWeight: 900,
                                color: "#fff",
                                lineHeight: 1.2
                            }}
                        >
                            Home Work Showcase
                        </Typography>

                        <Typography
                            sx={{
                                fontSize: { xs: "1rem", md: "1.2rem" },
                                color: "rgba(255, 255, 255, 0.9)",
                                maxWidth: "600px",
                                lineHeight: 1.6
                            }}
                        >
                            A glimpse of the work our students are proud to share
                        </Typography>

                        <Box sx={{ textAlign: "center", mt: 2 }}>
                            <Typography sx={{ fontSize: "2rem", fontWeight: 900, color: "#fff", lineHeight: 1 }}>
                                {totalRecords}+
                            </Typography>
                            <Typography sx={{ fontSize: "0.9rem", color: "rgba(255, 255, 255, 0.8)", mt: 0.5 }}>
                                Submissions Shared
                            </Typography>
                        </Box>
                    </Stack>
                </Container>
            </Box>

            {/* Submissions Section */}
            <Box sx={{ py: { xs: 6, md: 10 }, background: "#f5f5f5" }}>
                <Container maxWidth="lg">
                    <Stack spacing={3}>
                        {/* Filters */}
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Search by student or title..."
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                onKeyDown={handleSearchSubmit}
                                sx={{ bgcolor: "#fff" }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" color="action" />
                                        </InputAdornment>
                                    )
                                }}
                            />
                            <FormControl size="small" sx={{ minWidth: 200, bgcolor: "#fff" }}>
                                <InputLabel>Subject</InputLabel>
                                <Select
                                    label="Subject"
                                    value={subjectFilter}
                                    onChange={(e) => {
                                        setSubjectFilter(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                >
                                    <MenuItem value="all">All Subjects</MenuItem>
                                    {subjectOptions.map((s) => (
                                        <MenuItem key={s.subjectid} value={s.subjectid}>
                                            {s.subject_name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Stack>

                        {/* Grid */}
                        {loading ? (
                            <Box sx={{ textAlign: "center", py: 8 }}>
                                <Typography sx={{ fontSize: "1.2rem", color: theme.palette.text.secondary }}>
                                    Loading home work...
                                </Typography>
                            </Box>
                        ) : submissions.length === 0 ? (
                            <Box sx={{ textAlign: "center", py: 8 }}>
                                <Typography sx={{ fontSize: "1.2rem", color: theme.palette.text.secondary }}>
                                    No home work found
                                </Typography>
                            </Box>
                        ) : (
                            <Grid container spacing={3}>
                                {submissions.map((row) => (
                                    <Grid item xs={12} sm={6} md={4} key={row.submissionid}>
                                        <Card
                                            onClick={() => navigate(`/homework/${row.submissionid}`)}
                                            sx={{
                                                height: "100%",
                                                background: "#fff",
                                                borderRadius: 3,
                                                overflow: "hidden",
                                                transition: "all 0.3s ease",
                                                cursor: "pointer",
                                                boxShadow: "0 2px 12px rgba(0, 0, 0, 0.06)",
                                                "&:hover": {
                                                    transform: "translateY(-8px)",
                                                    boxShadow: `0 12px 32px ${row.color}25`
                                                }
                                            }}
                                        >
                                            <Box sx={{ height: 200, bgcolor: "grey.200" }}>
                                                {row.sub_video ? (
                                                    <video
                                                        src={row.sub_video}
                                                        controls
                                                        controlsList="nodownload noremoteplayback"
                                                        disablePictureInPicture
                                                        style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                                                    />
                                                ) : row.sub_image ? (
                                                    <img src={row.sub_image} alt={row.sub_title || "Home work submission"} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                                                ) : (
                                                    <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                                                        <Assignment sx={{ fontSize: 40, color: "text.disabled" }} />
                                                    </Box>
                                                )}
                                            </Box>
                                            <CardContent sx={{ p: 2.5 }}>
                                                <Stack spacing={1.5}>
                                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                        <Typography
                                                            sx={{
                                                                fontSize: "0.95rem",
                                                                fontWeight: 700,
                                                                color: theme.palette.text.primary,
                                                                overflow: "hidden",
                                                                textOverflow: "ellipsis",
                                                                whiteSpace: "nowrap"
                                                            }}
                                                        >
                                                            {row.student_name}
                                                        </Typography>
                                                        <Chip
                                                            label={row.subject_name}
                                                            size="small"
                                                            sx={{ height: 20, fontSize: "0.65rem", fontWeight: 600, background: `${row.color}15`, color: row.color, border: "none" }}
                                                        />
                                                    </Stack>

                                                    {row.sub_title && (
                                                        <Typography sx={{ fontSize: "0.9rem", fontWeight: 600, color: theme.palette.text.primary }} noWrap>
                                                            {row.sub_title}
                                                        </Typography>
                                                    )}

                                                    <Stack direction="row" spacing={2}>
                                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                                            <ThumbUpOutlined sx={{ fontSize: "1rem", color: theme.palette.text.secondary }} />
                                                            <Typography variant="caption" color="text.secondary">
                                                                {row.like_count || 0}
                                                            </Typography>
                                                        </Stack>
                                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                                            <ChatBubbleOutline sx={{ fontSize: "1rem", color: theme.palette.text.secondary }} />
                                                            <Typography variant="caption" color="text.secondary">
                                                                {row.comment_count || 0}
                                                            </Typography>
                                                        </Stack>
                                                    </Stack>
                                                </Stack>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        )}

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                                <Pagination
                                    count={totalPages}
                                    page={currentPage}
                                    onChange={handlePageChange}
                                    color="primary"
                                    size="large"
                                    sx={{ "& .MuiPaginationItem-root": { fontWeight: 600, fontSize: "1rem" } }}
                                />
                            </Box>
                        )}
                    </Stack>
                </Container>
            </Box>
        </>
    );
}
